import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OpsCommand } from './components/OpsCommand/OpsCommand';
import { useOpsAccessibleRestaurants } from './components/OpsCommand/hooks/useOpsCommand';
import { Login } from './components/Login';
import { WhatsNew } from './components/WhatsNew';
import { AuthProvider, useAuth } from './lib/auth';
import { getLocalLastScope } from './lib/clientConfig';
import './app.css';

// Fallback used only when auth is disabled (dev/demo) and nothing else resolves.
// Logged-in users never land here — they get alphabetical-first accessible or
// their last localStorage scope. Override per deployment with
// VITE_DEFAULT_RESTAURANT_ID.
const AUTH_DISABLED_DEFAULT_RESTAURANT_ID =
  (import.meta.env.VITE_DEFAULT_RESTAURANT_ID as string | undefined) ||
  '24uhAsvog7hRuxsbX';

function readUrlRestaurantId(): string | null {
  const match = window.location.pathname.match(/^\/r\/([^/?#]+)/);
  if (match) {
    return decodeURIComponent(match[1]);
  }
  return new URLSearchParams(window.location.search).get('restaurant');
}

/** /whats-new: the recorded product tour, a page of its own outside any restaurant. */
function isWhatsNewPath(): boolean {
  return window.location.pathname.replace(/\/+$/, '') === '/whats-new';
}

function GoogleOAuthCallback() {
  const { t } = useTranslation('common');
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payload = {
      type: 'aleno-google-oauth',
      ok: params.get('oauth') === 'ok',
      authId: params.get('authId'),
      kind: params.get('kind'),
      reason: params.get('reason'),
    };
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(payload, window.location.origin);
      window.close();
      return;
    }
    const next = new URLSearchParams();
    next.set('m', 'growth');
    next.set('gv', 'setup');
    next.set('oauth', payload.ok ? 'ok' : 'error');
    if (payload.authId) next.set('authId', payload.authId);
    if (payload.kind) next.set('kind', payload.kind);
    if (payload.reason) next.set('reason', String(payload.reason));
    window.location.replace(`/?${next.toString()}`);
  }, []);
  return (
    <section className="aleno3-container">
      <div className="aleno3-auth-loading">{t('finishingGoogle')}</div>
    </section>
  );
}

function AuthedApp() {
  const { t } = useTranslation('common');
  const { status, authDisabled, user, restaurantIds: meRestaurantIds } = useAuth();
  if (window.location.pathname.startsWith('/oauth/google/callback')) {
    return <GoogleOAuthCallback />;
  }
  if (isWhatsNewPath()) {
    if (status === 'loading') {
      return (
        <section className="aleno3-container">
          <div className="aleno3-auth-loading">{t('loading')}</div>
        </section>
      );
    }
    return status === 'anon' ? <Login /> : <WhatsNew />;
  }
  const [override, setOverride] = useState<string | null>(null);

  const scopeOwnerId = user?.id ?? (authDisabled ? 'auth-disabled' : null);
  const unrestricted = authDisabled || !!user?.isSuperUser;
  const fetchAccessible = status === 'authed';
  const accessible = useOpsAccessibleRestaurants(fetchAccessible);
  const accessibleList = accessible.data?.restaurants ?? [];
  const accessibleIds = useMemo(
    () => new Set(accessibleList.map(r => r.id)),
    [accessibleList],
  );
  const accessibleReady = !fetchAccessible || accessible.data != null;

  useEffect(() => {
    const onPop = () => setOverride(readUrlRestaurantId());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const switchRestaurant = useCallback((id: string) => {
    // Keep share/scope query params — wiping them used to drop group:/outlet:
    // filters on single-restaurant Apply from the scope picker.
    const search = window.location.search || '';
    window.history.pushState(
      {},
      '',
      `/r/${encodeURIComponent(id)}${search}`,
    );
    setOverride(id);
  }, []);

  const resolved = useMemo(() => {
    const urlId = readUrlRestaurantId();
    const last = scopeOwnerId ? getLocalLastScope(scopeOwnerId) : null;
    // Path-only /r/:id still restores lastScope's group/outlet when it matches.
    // Share-query regionFilter is applied inside OpsCommand (hasShareParams).
    const regionForRestaurant = (id: string) =>
      last?.restaurantId === id ? last.regionFilter : undefined;

    // 1) URL / deep link wins — but scoped users must not accept unknown ids
    // before the accessible catalog (or /me) can authorize them.
    if (urlId) {
      if (unrestricted) {
        return {
          restaurantId: urlId,
          initialRegionFilter: regionForRestaurant(urlId),
          ready: true,
        };
      }
      if (meRestaurantIds.includes(urlId)) {
        return {
          restaurantId: urlId,
          initialRegionFilter: regionForRestaurant(urlId),
          ready: true,
        };
      }
      if (!accessibleReady) {
        return {
          restaurantId: null as string | null,
          initialRegionFilter: undefined as string | null | undefined,
          ready: false,
        };
      }
      if (accessibleIds.has(urlId)) {
        return {
          restaurantId: urlId,
          initialRegionFilter: regionForRestaurant(urlId),
          ready: true,
        };
      }
      // Unauthorized URL id — fall through to last / alphabetical.
    }

    // 2) Last localStorage scope for this user.
    if (last?.restaurantId) {
      if (unrestricted) {
        return {
          restaurantId: last.restaurantId,
          initialRegionFilter: last.regionFilter,
          ready: true,
        };
      }
      if (meRestaurantIds.includes(last.restaurantId)) {
        return {
          restaurantId: last.restaurantId,
          initialRegionFilter: last.regionFilter,
          // Paint immediately from /me; catalog confirms access when ready.
          ready: true,
        };
      }
      if (!accessibleReady) {
        return {
          restaurantId: null as string | null,
          initialRegionFilter: undefined as string | null | undefined,
          ready: false,
        };
      }
      if (accessibleIds.has(last.restaurantId)) {
        return {
          restaurantId: last.restaurantId,
          initialRegionFilter: last.regionFilter,
          ready: true,
        };
      }
    }

    // 3) Alphabetical first accessible restaurant (server sorts by name).
    if (accessibleList.length > 0) {
      return {
        restaurantId: accessibleList[0]!.id,
        initialRegionFilter: 'all' as string | null,
        ready: true,
      };
    }

    // Scoped first-login: wait for the named accessible list so alphabetical
    // first is correct. Superusers always need that list.
    if (!accessibleReady) {
      return {
        restaurantId: null as string | null,
        initialRegionFilter: undefined as string | null | undefined,
        ready: false,
      };
    }

    if (authDisabled) {
      return {
        restaurantId: AUTH_DISABLED_DEFAULT_RESTAURANT_ID,
        initialRegionFilter: 'all' as string | null,
        ready: true,
      };
    }

    // Accessible catalog loaded with zero restaurants.
    return {
      restaurantId: null as string | null,
      initialRegionFilter: undefined as string | null | undefined,
      ready: true,
    };
  }, [
    accessibleList,
    accessibleIds,
    accessibleReady,
    authDisabled,
    meRestaurantIds,
    scopeOwnerId,
    unrestricted,
  ]);

  if (status === 'loading') {
    return (
      <section className="aleno3-container">
        <div className="aleno3-auth-loading">{t('loading')}</div>
      </section>
    );
  }

  if (status === 'anon') {
    return <Login />;
  }

  const restaurantId = override ?? resolved.restaurantId;
  if (!restaurantId && resolved.ready) {
    return (
      <section className="aleno3-container">
        <div className="aleno3-auth-loading">
          {t('noRestaurants')}
        </div>
      </section>
    );
  }
  if (!restaurantId || !resolved.ready) {
    return (
      <section className="aleno3-container">
        <div className="aleno3-auth-loading">{t('loadingRestaurants')}</div>
      </section>
    );
  }

  return (
    <section className="aleno3-container">
      <OpsCommand
        restaurantId={restaurantId}
        onSwitchRestaurant={switchRestaurant}
        initialRegionFilter={
          override ? undefined : resolved.initialRegionFilter
        }
        scopeOwnerId={scopeOwnerId}
      />
    </section>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AuthedApp />
    </AuthProvider>
  );
}
